import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Users, ArrowRight, X, Search, Check, Ban, MapPin, Hammer, Shield, Bell } from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/Header';
import { api, formatError } from '../lib/api';
import { useAuth } from '../context/AuthContext';

/**
 * /portfolio/panel — Contractor Panel Manager
 * Strata/Agencies invite tradies from the public directory into their private panel.
 */
export default function ContractorPanel() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login?redirect=/portfolio/panel');
      return;
    }
    if (user.role !== 'agent' && user.role !== 'admin' && user.role !== 'tradie') {
      toast.error('Contractor panel access is for property professionals.');
      navigate('/portfolio');
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.pnPanelList();
      setMembers(r.data || []);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  const act = async (id, action) => {
    try {
      await api.pnPanelAction(id, action);
      toast.success(`Marked as ${action}.`);
      load();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  if (!user) return null;

  const isTradieView = user.role === 'tradie';

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="contractor-panel-page">
      <Header />
      <div className="mx-auto max-w-6xl px-6 py-10 md:px-10">
        <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[#D96C4E]">Property Passport · Contractor Panel</div>
            <h1 className="mt-2 font-heading text-4xl font-bold tracking-tight text-[#1C1B1A] md:text-5xl">
              {isTradieView ? 'Panel invitations' : 'Contractor Panel Manager'}
            </h1>
            <p className="mt-3 max-w-2xl text-sm text-[#5C5B5A]">
              {isTradieView
                ? 'Property professionals who want you on their private panels. Accept or decline each invitation.'
                : 'Invite tradies from the public network into your private preferred, emergency or backup panel. Bulk work orders and priority dispatch.'}
            </p>
          </div>
          {!isTradieView && (
            <button onClick={() => setShowInvite(true)} className="btn-primary" data-testid="panel-invite-btn">
              <Users size={14} /> Invite tradie
            </button>
          )}
        </div>

        <section className="mt-10">
          {loading ? (
            <div className="border border-dashed border-[#E5E4E0] bg-white p-10 text-center text-sm text-[#8A8988]" data-testid="panel-loading">Loading panel…</div>
          ) : members.length === 0 ? (
            <div className="border border-dashed border-[#E5E4E0] bg-white p-12 text-center" data-testid="panel-empty">
              <Users size={32} className="mx-auto text-[#8A8988]" />
              <h3 className="mt-4 font-heading text-xl font-bold text-[#1C1B1A]">
                {isTradieView ? 'No panel invitations yet' : 'No panel members yet'}
              </h3>
              <p className="mt-2 text-sm text-[#5C5B5A]">
                {isTradieView
                  ? 'When strata or agencies invite you, they will appear here.'
                  : 'Search the public network and send your first invitation.'}
              </p>
              {!isTradieView && (
                <button onClick={() => setShowInvite(true)} className="btn-primary mt-6 inline-flex" data-testid="panel-empty-invite">
                  <Users size={14} /> Invite tradie
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {members.map((m) => (
                <PanelCard key={m.id} member={m} onAction={act} isTradie={isTradieView} />
              ))}
            </div>
          )}
        </section>
      </div>

      {showInvite && <InviteModal onClose={() => setShowInvite(false)} onInvited={() => { setShowInvite(false); load(); }} />}
    </div>
  );
}

function PanelCard({ member, onAction, isTradie }) {
  const tone = member.status === 'approved' ? 'green' :
               member.status === 'rejected' ? 'red' :
               member.status === 'removed' ? 'muted' : 'amber';
  return (
    <div className="border border-[#E5E4E0] bg-white p-5" data-testid={`panel-member-${member.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center bg-[#1A362D] text-white">
              <Hammer size={16} />
            </div>
            <div>
              <div className="font-heading text-base font-bold text-[#1C1B1A]">{member.tradie_business_name}</div>
              <div className="mt-0.5 flex items-center gap-1 text-xs text-[#8A8988]">
                <MapPin size={11} /> {member.tradie_suburb} · {member.tradie_category}
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <TierBadge tier={member.tier} />
          <StatusPill tone={tone} text={member.status} />
        </div>
      </div>
      {member.notes && (
        <div className="mt-3 border-l-2 border-[#E5E4E0] pl-3 text-xs italic text-[#5C5B5A]">"{member.notes}"</div>
      )}
      <div className="mt-4 flex items-center justify-between border-t border-[#E5E4E0] pt-3 text-xs text-[#5C5B5A]">
        <span>Invited {new Date(member.created_at).toLocaleDateString('en-AU')}</span>
        {member.status === 'pending' && (
          <div className="flex gap-2">
            {isTradie ? (
              <>
                <button onClick={() => onAction(member.id, 'reject')} className="btn-ghost !py-1 !px-2 !text-[11px]" data-testid={`decline-${member.id}`}>
                  <Ban size={12} /> Decline
                </button>
                <button onClick={() => onAction(member.id, 'approve')} className="btn-primary !py-1 !px-2 !text-[11px]" data-testid={`accept-${member.id}`}>
                  <Check size={12} /> Accept
                </button>
              </>
            ) : (
              <>
                <button onClick={() => onAction(member.id, 'reject')} className="btn-ghost !py-1 !px-2 !text-[11px]" data-testid={`reject-${member.id}`}>Reject</button>
                <button onClick={() => onAction(member.id, 'approve')} className="btn-primary !py-1 !px-2 !text-[11px]" data-testid={`approve-${member.id}`}>Approve</button>
              </>
            )}
          </div>
        )}
        {member.status === 'approved' && !isTradie && (
          <button onClick={() => onAction(member.id, 'remove')} className="btn-ghost !py-1 !px-2 !text-[11px]" data-testid={`remove-${member.id}`}>Remove</button>
        )}
      </div>
    </div>
  );
}

function TierBadge({ tier }) {
  const cls = tier === 'emergency' ? 'border-[#D96C4E] bg-[#D96C4E] text-white' :
              tier === 'preferred' ? 'border-[#1A362D] bg-[#1A362D] text-white' :
              'border-[#8A8988] bg-[#8A8988] text-white';
  return <span className={`border px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.14em] ${cls}`}>{tier}</span>;
}

function StatusPill({ tone, text }) {
  const cls = tone === 'green' ? 'border-[#1A362D] bg-[#1A362D]/[0.08] text-[#1A362D]'
            : tone === 'red' ? 'border-[#D96C4E] bg-[#D96C4E]/[0.08] text-[#D96C4E]'
            : tone === 'amber' ? 'border-amber-600 bg-amber-50 text-amber-800'
            : 'border-[#E5E4E0] bg-[#F3F2EE] text-[#5C5B5A]';
  return <span className={`border px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.14em] ${cls}`}>{text}</span>;
}

function InviteModal({ onClose, onInvited }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);
  const [tier, setTier] = useState('preferred');
  const [notes, setNotes] = useState('');

  const doSearch = async (e) => {
    e?.preventDefault();
    if (!q) return;
    setBusy(true);
    try {
      const r = await api.listTrades({ q });
      setResults(r.data.slice(0, 12));
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    if (!selected) return;
    try {
      await api.pnPanelInvite({ tradie_id: selected.id, tier, notes: notes || undefined });
      toast.success(`Invitation sent to ${selected.business_name}.`);
      onInvited();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4 pt-6" data-testid="panel-invite-modal">
      <div className="w-full max-w-2xl border border-[#1C1B1A] bg-white">
        <div className="flex items-center justify-between border-b border-[#E5E4E0] px-6 py-4">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.28em] text-[#D96C4E]">Panel · Invite tradie</div>
            <h3 className="font-heading text-xl font-bold text-[#1C1B1A]">Find and invite from the public network</h3>
          </div>
          <button onClick={onClose} className="text-[#8A8988] hover:text-[#1C1B1A]" data-testid="close-panel-invite"><X size={18} /></button>
        </div>
        <div className="space-y-4 p-6">
          <form onSubmit={doSearch} className="flex gap-2">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by trade or business name…" className="input-base flex-1" data-testid="panel-search-input" />
            <button type="submit" disabled={busy} className="btn-primary" data-testid="panel-search-btn"><Search size={14} /> Search</button>
          </form>

          {results.length > 0 && (
            <div className="space-y-2">
              {results.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setSelected(t)}
                  className={`flex w-full items-center justify-between border p-3 text-left transition-colors ${selected?.id === t.id ? 'border-[#1A362D] bg-[#1A362D]/[0.06]' : 'border-[#E5E4E0] hover:border-[#1A362D]'}`}
                  data-testid={`invite-result-${t.id}`}
                >
                  <div>
                    <div className="font-semibold text-[#1C1B1A]">{t.business_name}</div>
                    <div className="mt-0.5 text-xs text-[#8A8988]">{t.category} · {t.suburb}</div>
                  </div>
                  {selected?.id === t.id && <Check size={16} className="text-[#1A362D]" />}
                </button>
              ))}
            </div>
          )}

          {selected && (
            <div className="border-t border-[#E5E4E0] pt-4">
              <div className="mb-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#5C5B5A]">Invitation details</div>
              <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-[#5C5B5A]">Panel tier</div>
              <div className="flex flex-wrap gap-2">
                {['preferred', 'emergency', 'backup'].map((t) => (
                  <button
                    key={t}
                    onClick={() => setTier(t)}
                    className={`border px-3 py-1.5 text-xs font-medium ${tier === t ? 'border-[#1A362D] bg-[#1A362D] text-white' : 'border-[#E5E4E0] text-[#1C1B1A]'}`}
                    data-testid={`tier-${t}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional notes (e.g. reliability record, past jobs together)"
                className="input-base mt-3 min-h-[80px]"
                data-testid="panel-notes"
              />
              <button onClick={submit} className="btn-primary mt-4 w-full justify-center" data-testid="panel-invite-submit">
                Send invitation to {selected.business_name} <ArrowRight size={14} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
